import { CloseOutlined, ViewList } from "@mui/icons-material";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Box,
    Divider,
    CircularProgress,
    Grid,
    Paper,
    Chip,
    Stack,
    TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { useGetPaymentHistoryListQuery } from "../../store/common/commonApis";
import { capitalizeFirstLetter, handleDateTimeDefault, JsonDecode } from "../../helpers/utils";
import { EnrollActivityAddDrawer } from "../admin/enroll-activity-manager/EnrollActivityAddDrawer";
import { useGetBookingListQuery } from "../../store/booking/bookingApis";
import Button from "./Button";
import { getType } from "@reduxjs/toolkit";
import { getCookie, isAuth } from "../../helpers/cookies";
import { baseUrl } from "../../helpers/constants";
import { format } from "date-fns";
import Input from "./Input";
import { Fragment } from "react";

export const MemberHistoryModal = ({ show, close, getActivityList, getMembersList }) => {
    const [showDrawer, setShowDrawer] = React.useState(false);
    const [bookingInitalValue, setBookingInitalValue] = React.useState(false);
    const [bookingId, setBookingId] = React.useState(null);

    const [editingIndex, setEditingIndex] = React.useState(null);
    const [differenceAmount, setEditDifferenceAmount] = React.useState(0);
    const [yearFilter, setYearFilter] = React.useState(new Date().getFullYear());
    const [filteredData, setFilteredData] = React.useState([]);
    const [editedRemark, setEditedRemark] = React.useState("");
    const [bookingType, setBookingType] = React.useState("enrollment");

    const { data: bookings, isLoading: bookingsLoading } = 
    useGetBookingListQuery(
        {
            pageNo: 0,
            limit: 1,
            sortBy: -1,
            booking_id: bookingId,
            type: bookingType,
        },
        {
            skip: !bookingId,
        },
    );

    useEffect(() => {
        if (bookings && bookings?.result && bookings?.result.length > 0) {
            setBookingInitalValue(bookings?.result[0]);
            setShowDrawer(true);
        }
    }, [bookings]);

    const {
        isLoading,
        data: paymentHistory,
        refetch,
        isFetching,
    } = useGetPaymentHistoryListQuery({
        pageNo: 0,
        limit: 100,
        sortBy: -1,
        sortField: "createdAt",
        keywords: show,
        filter_by: "member_id",
        payment_status: "Success",
    });

    const years = [2024, 2025, 2026];

    useEffect(() => {
        if (yearFilter) {
            setFilteredData(filteredData?.filter((item) => item.createdAt.includes(yearFilter)));
        }
    }, [yearFilter]);

    useEffect(() => {
        if (paymentHistory && paymentHistory?.result && paymentHistory?.result.length > 0) {
            setFilteredData(paymentHistory?.result?.filter((item) => item.createdAt.includes(yearFilter)));
        }
    }, [paymentHistory, yearFilter]);

    const getTotalAmount = useCallback(
        (type) => {
            let total = 0;

            if (filteredData) {
                filteredData.forEach((obj) => {
                    if (type === "membership") {
                        total += getMembershipBreakdown(obj).reduce(
                            (acc, member) => acc + Number(member?.amount || 0),
                            0
                        );
                    }
                    else if (type === "enrollment") {
                        const bookingsForAmount = isRenewalPayment(obj)
                            ? obj?.bookings_data?.filter(
                                (booking) => booking?.status === true
                            )
                            : obj?.bookings_data;

                        total += bookingsForAmount?.reduce((acc, cur) => {
                            return acc + Number(cur?.total_amount || 0);
                        }, 0);
                    }
                });
            }

            return total;
        },
        [filteredData]
    );

    const getSingleAmount = (obj, type) => {
        const membershipPlan = obj?.plans_data?.find(
            (plan) => plan?.plan_type === "membership"
        );

        const membershipAmount = Number(membershipPlan?.amount || 0);

        if (type === "membership") {
            if (
                Array.isArray(obj?.membership_breakdown) &&
                obj.membership_breakdown.length > 0
            ) {
                return obj.membership_breakdown.reduce(
                    (total, member) => total + Number(member?.amount || 0),
                    0
                );
            }

            // Legacy payment fallback
            return getMembershipBreakdown(obj).reduce(
                (total, member) => total + Number(member?.amount || 0),
                0
            );
        }

        if (type === "booking") {
            return Number(
                obj?.amount_paid ??
                obj?.bookings_data?.[0]?.total_amount ??
                obj?.bookings_data?.[0]?.fees_breakup?.price ??
                0
            );
        }

        if (type === "enrollment") {
            const hasMembership = obj?.plans_data?.some(
                (plan) => plan?.plan_type === "membership"
            );

            const hasEnrollment = obj?.plans_data?.some(
                (plan) => plan?.plan_type === "enrollment"
            );

            // Both Membership + Enrollment
            if (hasMembership && hasEnrollment) {
                return Math.max(
                    Number(obj?.amount_paid || 0) - membershipAmount,
                    0
                );
            }

            // Enrollment only
            if (hasEnrollment) {
                return Number(obj?.amount_paid || 0);
            }

            return 0;
        }



        return 0;
    };

    const getEnrollmentBookingAmount = (booking) => {
        return Number(
                booking?.total_amount ??
                booking?.fees_breakup?.amount ??
                booking?.fees_breakup?.price ??
                0
            );
        };

    const getItemEnrollmentAmount = (item) => {
        const bookings = getEnrollmentBookingsForDisplay(item);

        return bookings.reduce((total, booking) => {
            return total + Number(booking?.total_amount || 0);
        }, 0);
    };

    const isRenewalPayment = (item) => {
        return item?.remarks?.startsWith("Renew Payment");
    };

    // New renewal created by the new logic:
    // payment history contains BOTH old booking ID and new booking ID.
    const isNewRenewalPayment = (item) => {
        return (
            isRenewalPayment(item) &&
            Array.isArray(item?.booking_id) &&
            item.booking_id.length > 1
        );
    };

    // Get the correct enrollment plan for payment history.
    //
    // OLD / LEGACY PAYMENT:
    // Use plans_data because the old booking may have been modified later.
    //
    // NEW RENEWAL:
    // Use the active booking's fees_breakup because the new renewal
    // creates a new booking with the new plan.
    const getEnrollmentPlanForDisplay = (item, booking) => {
        const enrollmentPlans = Array.isArray(item?.plans_data)
            ? item.plans_data.filter(
                (plan) => plan?.plan_type === "enrollment"
            )
            : [];

        // Old/original enrollment payment
        if (!isRenewalPayment(item)) {
            return (
                booking?.fees_breakup ||
                enrollmentPlans[0] ||
                {}
            );
        }

        // New renewal payment
        if (isNewRenewalPayment(item)) {
            return (
                booking?.fees_breakup ||
                enrollmentPlans[enrollmentPlans.length - 1] ||
                {}
            );
        }

        // Legacy renewal payment
        // Old renewal logic reused the same booking, so use the
        // historical plan stored inside payment history.
        return (
            enrollmentPlans[0] ||
            booking?.fees_breakup ||
            {}
        );
    };

    // Check whether this booking was renewed again later.
    //
    // This is mainly for OLD payment records where the old renewal
    // logic reused the same booking.
    const wasRenewedLater = (item, booking) => {
        if (!booking?._id) {
            return false;
        }

        const currentPaymentDate = new Date(item?.createdAt);

        if (Number.isNaN(currentPaymentDate.getTime())) {
            return false;
        }

        const allPayments = paymentHistory?.result || [];

        return allPayments.some((otherPayment) => {
            if (!isRenewalPayment(otherPayment)) {
                return false;
            }

            // Don't compare the payment with itself
            if (
                String(otherPayment?._id) === String(item?._id)
            ) {
                return false;
            }

            const otherPaymentDate = new Date(
                otherPayment?.createdAt
            );

            if (Number.isNaN(otherPaymentDate.getTime())) {
                return false;
            }

            // Renewal must have happened AFTER this payment
            if (otherPaymentDate <= currentPaymentDate) {
                return false;
            }

            // Does the later renewal reference this old booking?
            return (
                Array.isArray(otherPayment?.booking_id) &&
                otherPayment.booking_id.some(
                    (id) =>
                        String(id) === String(booking?._id)
                )
            );
        });
    };

    // Final status to display in history
    const getEnrollmentDisplayStatus = (item, booking) => {
        // New renewal -> use actual new booking status
        if (isNewRenewalPayment(item)) {
            return booking?.status === true;
        }

        // Old payment whose booking was reused by a later renewal
        if (wasRenewedLater(item, booking)) {
            return false;
        }

        return booking?.status === true;
    };

    // Which bookings should be displayed?
    const getEnrollmentBookingsForDisplay = (item) => {
        const bookings = item?.bookings_data || [];

        // NEW renewal:
        // show only the newly-created active booking.
        if (isNewRenewalPayment(item)) {
            return bookings.filter(
                (booking) => booking?.status === true
            );
        }

        // OLD data:
        // keep the booking visible so its historical information
        // can be displayed correctly.
        return bookings;
    };

    

    const isEventPayment = (item) => {
        return item?.remarks?.startsWith("Payment for event booking");
    };

    const getMemberData = (item) => {
        if (Array.isArray(item?.member_data)) {
            return item.member_data[0] || {};
        }

        return item?.member_data || {};
    };

    const getPaymentMember = (item) => {
        const memberData = getMemberData(item);

        // Secondary member payment
        if (item?.secondary_member_id) {
            const familyDetails = memberData?.family_details || [];

            const secondaryMember = familyDetails.find((member) =>
                String(member?._id) === String(item.secondary_member_id) ||
                String(member?.id) === String(item.secondary_member_id) ||
                String(member?.secondary_member_id) === String(item.secondary_member_id)
            );

            if (secondaryMember) {
                return {
                    type: "Secondary Member",
                    id:
                        secondaryMember?.id ||
                        secondaryMember?.secondary_member_id ||
                        secondaryMember?._id ||
                        "-",
                    name: secondaryMember?.name || "-",
                    phone: secondaryMember?.mobile || secondaryMember?.phone || "-",
                };
            }
        }

        // Primary member payment
        return {
            type: "Primary Member",
            id: memberData?.member_id || "-",
            name: memberData?.name || "-",
            phone: memberData?.mobile || memberData?.phone || "-",
        };
    };

    const getMembershipBreakdown = (item) => {
        const memberData = getMemberData(item);

        // New payment records: use backend-stored breakup
        if (
            Array.isArray(item?.membership_breakdown) &&
            item.membership_breakdown.length > 0
        ) {
            return item.membership_breakdown;
        }

        // Legacy payment records: reconstruct from existing data
        const membershipPlan = item?.plans_data?.find(
            (plan) => plan?.plan_type === "membership"
        );

        if (!membershipPlan) {
            return [];
        }

        // Secondary member renewal/payment
        if (item?.secondary_member_id) {
            const secondaryMember = memberData?.family_details?.find(
                (member) =>
                    String(member?._id) === String(item.secondary_member_id) ||
                    String(member?.id) === String(item.secondary_member_id) ||
                    String(member?.secondary_member_id) === String(item.secondary_member_id)
            );

            if (secondaryMember) {
                return [
                    {
                        type: "Secondary Member",
                        id:
                            secondaryMember?.id ||
                            secondaryMember?.secondary_member_id ||
                            secondaryMember?._id ||
                            "-",
                        name: secondaryMember?.name || "-",
                        amount: Number(item?.amount_paid || 0),
                    },
                ];
            }
        }

        const primaryAmount = Number(membershipPlan?.amount || 0);

        const breakdown = [
            {
                type: "Primary Member",
                id: memberData?.member_id || "-",
                name: memberData?.name || "-",
                amount: primaryAmount,
            },
        ];

        let remainingAmount =
            Number(item?.amount_paid || 0) - primaryAmount;

        if (remainingAmount <= 0) {
            return breakdown;
        }

        const familyDetails = Array.isArray(memberData?.family_details)
            ? memberData.family_details
            : [];

        for (const family of familyDetails) {
            if (remainingAmount <= 0) {
                break;
            }

            const isDependent =
                family?.is_dependent === true ||
                family?.is_dependent === "true";

            const familyAmount = isDependent
                ? Number(
                    family?.plans?.dependent_member_price ??
                        membershipPlan?.dependent_member_price ??
                        0
                )
                : Number(
                    family?.plans?.non_dependent_member_price ??
                        membershipPlan?.non_dependent_member_price ??
                        0
                );

            if (familyAmount <= 0) {
                continue;
            }

            if (familyAmount <= remainingAmount) {
                breakdown.push({
                    type: "Secondary Member",
                    id:
                        family?.id ||
                        family?.secondary_member_id ||
                        family?._id ||
                        "-",
                    name: family?.name || "-",
                    amount: familyAmount,
                });

                remainingAmount -= familyAmount;
            }
        }

        return breakdown;
    };



    const getEventMember = (item) => {
        const memberId =
            item?.remarks?.match(/member id\s*:\s*([^,\s]+)/i)?.[1]?.trim();

        if (!memberId) {
            return {
                type: "Primary Member",
                id: item?.member_data?.member_id || "-",
                name: item?.member_data?.name || "-",
            };
        }

        if (
            item?.member_data?.member_id?.toString() ===
            memberId.toString()
        ) {
            return {
                type: "Primary Member",
                id: item.member_data.member_id,
                name: item.member_data.name || "-",
            };
        }

        const familyMember = item?.member_data?.family_details?.find(
            (member) =>
                member?.id?.toString() === memberId.toString() ||
                member?.secondary_member_id?.toString() === memberId.toString() ||
                member?._id?.toString() === memberId.toString()
        );

        if (familyMember) {
            return {
                type: "Secondary Member",
                id:
                    familyMember.id ||
                    familyMember.secondary_member_id ||
                    familyMember._id ||
                    "-",
                name: familyMember.name || "-",
            };
        }

        return {
            type: "Member",
            id: memberId,
            name: "-",
        };
    };

    const getBookingMembers = (item, booking) => {
        const primaryMember = {
            type: "Primary Member",
            id: item?.member_data?.member_id || "-",
            name: item?.member_data?.name || "-",
        };

        const familyDetails = item?.member_data?.family_details || [];
        const familyRefs = Array.isArray(booking?.family_member)
            ? booking.family_member
            : [];

        // No secondary member specified = booking is for primary member
        if (familyRefs.length === 0) {
            return [primaryMember];
        }

        const secondaryMembers = familyRefs
            .map((fam) => {
                // family_member is already an object
                if (
                    fam &&
                    typeof fam === "object" &&
                    (fam.name || fam.id || fam._id || fam.secondary_member_id)
                ) {
                    return {
                        type: "Secondary Member",
                        id:
                            fam.id ||
                            fam.secondary_member_id ||
                            fam._id ||
                            "-",
                        name: fam.name || "-",
                    };
                }

                // family_member is an ID/reference
                const famStr = fam?.toString?.();

                const found = familyDetails.find((fd) => {
                    if (!fd || !famStr) return false;

                    return (
                        (fd._id && fd._id.toString() === famStr) ||
                        (fd.id && fd.id.toString() === famStr) ||
                        (fd.secondary_member_id &&
                            fd.secondary_member_id.toString() === famStr)
                    );
                });

                if (found) {
                    return {
                        type: "Secondary Member",
                        id:
                            found.id ||
                            found.secondary_member_id ||
                            found._id?.toString() ||
                            "-",
                        name: found.name || "-",
                    };
                }

                return null;
            })
            .filter(Boolean);

        return secondaryMembers.length > 0
            ? secondaryMembers
            : [primaryMember];
    };

    const isEnrollmentItem = (item) => {
        return (
            item?.plans_data?.some(
                (plan) => plan?.plan_type === "enrollment"
            ) ||
            item?.bookings_data?.some(
                (booking) =>
                    booking?.type === "enrollment" ||
                    booking?.fees_breakup?.plan_type === "enrollment"
            )
        );
    };

    const getTypeLabel = (item) => {
        const plans = item?.plans_data || [];

        const hasMembership = plans.some(
            (p) => p?.plan_type === "membership"
        );

        const hasEnrollment = isEnrollmentItem(item);

        const hasEvent = isEventPayment(item);

        if (hasMembership && hasEnrollment) return "Both";
        if (hasMembership) return "Membership";
        if (hasEnrollment) return "Enrollment";
        if (item?.booking_type === "booking") return "Booking";
        if (hasEvent) return "Event";

        return "Unknown";
    };

    const updateRemarkAPI = async (id, data) => {
        try {
            const response = await fetch(`${baseUrl}/payment/update-remark`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getCookie("asmc_token")}`,
                },
                body: JSON.stringify({ _id: id, ...data }),
            });
            if (response.ok) {
                await response.json();
                refetch();
                setEditingIndex(null);
            }
        } catch (error) {
            console.error(error);
        }
    };

    if (isLoading || isFetching) {
        return (
            <Dialog open={Boolean(show)} onClose={close} maxWidth="md">
                <DialogTitle>Loading...</DialogTitle>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={Boolean(show)}
            onClose={() => {
                close();
                setFilteredData([]);
            }}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: 1200 }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">Payment History</Typography>
                    <Typography variant="subtitle2">
                        Total Membership Amount: Rs. {getTotalAmount("membership") || 0}
                    </Typography>
                    <Typography variant="subtitle2">
                        Total Enrollment Amount: Rs. {getTotalAmount("enrollment") || 0}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Paper sx={{ mb: 2 }}>
                    <Typography variant="h6">Filter by Year</Typography>
                    <Stack direction="row" spacing={1}>
                        {years.map((year, key) => {
                            return (
                                <Chip
                                    key={key}
                                    label={year}
                                    onClick={() => {
                                        setYearFilter(year);
                                    }}
                                    variant={yearFilter === year ? "filled" : "outlined"}
                                    color={"primary"}
                                />
                            );
                        })}
                    </Stack>
                </Paper>
                {!filteredData || filteredData?.length === 0 ? (
                    <Typography>No history available</Typography>
                ) : (
                    filteredData.map((item, index) => {
                        const paymentRes = JsonDecode(item.payment_response);
                        let isSecondary = item.bookings_data?.[0]?.family_member?.[0];

                        if (item?.secondary_member_id) {
                            isSecondary = item?.member_data?.family_details.find(
                                (member) => member._id === item?.secondary_member_id,
                            );
                        }
                        return (
                            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, borderLeft: "5px solid #1976d2" }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="h6">
                                                #{index + 1} —{" "}
                                                <Chip label={getTypeLabel(item)} size="medium" color="primary">
                                                    {getTypeLabel(item)}
                                                </Chip>
                                            </Typography>
                                        </Stack>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>

                                    {/* Member Info: show primary + any secondary/non-member players included in this payment */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            <strong>Members in this Payment</strong>
                                        </Typography>
                                        {/* Primary Member */}
                                        {(() => {
                                            const paymentMember = getPaymentMember(item);

                                            return (
                                                <Box
                                                    mb={1}
                                                    p={1}
                                                    border={1}
                                                    borderColor="grey.200"
                                                    borderRadius={1}
                                                >
                                                    <Typography>
                                                        <strong>{paymentMember.type}</strong>
                                                    </Typography>

                                                    <Typography>
                                                        <strong>ID:</strong> {paymentMember.id}
                                                    </Typography>

                                                    <Typography>
                                                        <strong>Name:</strong> {paymentMember.name}
                                                    </Typography>

                                                    <Typography>
                                                        <strong>Phone:</strong> {paymentMember.phone}
                                                    </Typography>
                                                </Box>
                                            );
                                        })()}

                                        {/* Derived players / secondary members from bookings_data */}
                                        {getEnrollmentBookingsForDisplay(item)?.map((booking, bi) => {
                                            // players can be non-member players or family_member references
                                            const players = booking.players || [];
                                            const familyRefs = Array.isArray(booking?.family_member)
                                                ? booking.family_member.filter(Boolean)
                                                : [];

                                            const resolveFamilyMember = (fam) => {
                                                // If fam is an object with a name, return it directly
                                                if (fam && typeof fam === "object" && (fam.name || fam.id || fam._id || fam.secondary_member_id)) {
                                                    return {
                                                        name: fam.name || fam.name === "" ? fam.name : null,
                                                        id: fam.id || fam.secondary_member_id || fam._id || null,
                                                    };
                                                }

                                                // Otherwise try to resolve against member_data.family_details (which may contain objects with _id and id)
                                                const familyDetails = item.member_data?.family_details || [];
                                                if (familyDetails && familyDetails.length > 0) {
                                                    const found = familyDetails.find((fd) => {
                                                        try {
                                                            if (!fd) return false;
                                                            if (fam && typeof fam === "object" && fam._id) {
                                                                return fd._id && fd._id.toString() === fam._id.toString();
                                                            }
                                                            // fam might be a plain id string (like 'S1-00001') or ObjectId string
                                                            const famStr = fam && fam.toString ? fam.toString() : null;
                                                            if (!famStr) return false;
                                                            if (fd._id && fd._id.toString() === famStr) return true;
                                                            if (fd.id && fd.id.toString() === famStr) return true;
                                                            if (fd.secondary_member_id && fd.secondary_member_id.toString() === famStr) return true;
                                                            return false;
                                                        } catch (e) {
                                                            return false;
                                                        }
                                                    });
                                                    if (found) {
                                                        return { name: found.name, id: found.id || found.secondary_member_id || (found._id && found._id.toString()) };
                                                    }
                                                }

                                                return null;
                                            };

                                            return (
                                                <Box key={bi} mb={1} p={1} border={1} borderColor="grey.100" borderRadius={1}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Booking #{booking.booking_id} — Amount: Rs. {getEnrollmentBookingAmount(booking)}
                                                    </Typography>
                                                    {familyRefs.length > 0 &&
                                                        familyRefs.map((fam, fi) => {
                                                            const fm = resolveFamilyMember(fam);
                                                            const name = fm ? fm.name || "Secondary Member" : (fam && fam.name) || "Secondary Member";
                                                            const memberId = fm ? fm.id || "-" : (fam && (fam.id || fam.secondary_member_id)) || "-";
                                                            return (
                                                                <Box key={fi} sx={{ ml: 1, mb: 0.5 }}>
                                                                    <Typography>
                                                                        <strong>Secondary:</strong> {name} ({memberId})
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}

                                                    {players.length > 0 &&
                                                        players.map((p, pi) => (
                                                            <Box key={pi} sx={{ ml: 1, mb: 0.5 }}>
                                                                <Typography>
                                                                    <strong>Player:</strong> {p.name || p.email || p.mobile} — CHSS: {p.chss_number || "-"}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                </Box>
                                            );
                                        })}
                                    </Grid>

                                    {/* Booking Details */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Booking & Activity</Typography>
                                        {isEventPayment(item) && (
                                            <Box
                                                mb={2}
                                                p={1}
                                                border={1}
                                                borderColor="grey.300"
                                                borderRadius={2}
                                                bgcolor="#e8f5e9"
                                            >
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Event Booking - Successful
                                                </Typography>

                                                {(() => {
                                                    const eventMember = getEventMember(item);

                                                    return (
                                                        <>
                                                            <Typography>
                                                                <strong>Booked For:</strong> {eventMember.type}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>ID:</strong> {eventMember.id}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>Name:</strong> {eventMember.name}
                                                            </Typography>
                                                        </>
                                                    );
                                                })()}

                                                <Typography>
                                                    <strong>Booking ID:</strong>{" "}
                                                    {item?.remarks?.match(/Booking Id:\s*([^,]+)/i)?.[1] || "-"}
                                                </Typography>

                                                <Typography>
                                                    <strong>Event:</strong>{" "}
                                                    {item?.remarks
                                                        ?.match(
                                                            /Event Name\s*:\s*([^,]+?)(?=,\s*Event Id|$)/i
                                                        )?.[1] || "-"}
                                                </Typography>

                                                <Typography>
                                                    <strong>Event ID:</strong>{" "}
                                                    {item?.remarks?.match(/Event Id\s*:\s*([^,]+)/i)?.[1] || "-"}
                                                </Typography>

                                                <Typography>
                                                    <strong>Amount:</strong> Rs. {item?.amount_paid || 0}
                                                </Typography>
                                            </Box>
                                        )}
                                        {item?.plans_data?.some(
                                            (plan) => plan?.plan_type === "membership"
                                        ) && (
                                            <Box
                                                mb={2}
                                                p={1}
                                                border={1}
                                                borderColor="grey.300"
                                                borderRadius={2}
                                                bgcolor="#e8f5e9"
                                            >
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Membership - Active
                                                </Typography>

                                                {getMembershipBreakdown(item).map((member, mi) => (
                                                    <Box
                                                        key={mi}
                                                        mb={1}
                                                        p={1}
                                                        border={1}
                                                        borderColor="grey.200"
                                                        borderRadius={1}
                                                        bgcolor="#f5f5f5"
                                                    >
                                                        <Typography>
                                                            <strong>Booked For:</strong>{" "}
                                                            {member.type}
                                                        </Typography>

                                                        <Typography>
                                                            <strong>ID:</strong>{" "}
                                                            {member.id}
                                                        </Typography>

                                                        <Typography>
                                                            <strong>Name:</strong>{" "}
                                                            {member.name}
                                                        </Typography>

                                                        <Typography>
                                                            <strong>Amount:</strong>{" "}
                                                            Rs. {member.amount}
                                                        </Typography>
                                                    </Box>
                                                ))}

                                                {item?.plans_data
                                                    ?.filter(
                                                        (plan) => plan?.plan_type === "membership"
                                                    )
                                                    ?.map((plan, pi) => (
                                                        <React.Fragment key={pi}>
                                                            <Typography>
                                                                <strong>Plan Name:</strong>{" "}
                                                                {plan?.plan_name || "-"}
                                                            </Typography>
                                                        </React.Fragment>
                                                    ))}
                                            </Box>
                                        )}
                                        {getEnrollmentBookingsForDisplay(item)?.map((booking, i) => {
                                            const displayStatus = getEnrollmentDisplayStatus(item, booking);
                                            const displayPlan = getEnrollmentPlanForDisplay(item, booking);
                                            return (
                                                <Box
                                                    key={i}
                                                    mb={2}
                                                    p={1}
                                                    border={1}
                                                    borderColor="grey.300"
                                                    borderRadius={2}
                                                    bgcolor={displayStatus ? "#e8f5e9" : "#ff756b"}
                                                >
                                                    <Typography variant="subtitle2" gutterBottom>
                                                    {booking?.type === "booking" && !isEnrollmentItem(item)
                                                        ? "Booking"
                                                        : "Enrollment"}{" "}
                                                    {i + 1} -{" "}
                                                    {displayStatus ? "Active" : "Inactive"}
                                                    </Typography>

                                                    {getBookingMembers(item, booking).map((member, mi) => (
                                                        <Box
                                                            key={mi}
                                                            mb={1}
                                                            p={1}
                                                            border={1}
                                                            borderColor="grey.200"
                                                            borderRadius={1}
                                                            bgcolor="#f5f5f5"
                                                        >
                                                            <Typography>
                                                                <strong>Booked For:</strong> {member.type}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>ID:</strong> {member.id}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>Name:</strong> {member.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}

                                                    <Typography>
                                                        <strong>
                                                            {booking?.type === "booking" && !isEnrollmentItem(item)
                                                                ? "Booking ID"
                                                                : "Enroll ID"}:
                                                        </strong>{" "}
                                                        {booking.booking_id}{" "}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setBookingType(
                                                                    booking?.type === "booking" && !isEnrollmentItem(item)
                                                                        ? "booking"
                                                                        : "enrollment"
                                                                );
                                                                setBookingId(booking.booking_id);
                                                            }}
                                                            loading={bookingsLoading}
                                                            fullWidth={false}
                                                        >
                                                            View
                                                        </Button>
                                                    </Typography>

                                                    {(() => {
                                                        const activity = item?.activity_data?.find(
                                                            (activity) =>
                                                                String(activity?._id) === String(booking?.activity_id)
                                                        );

                                                        return activity ? (
                                                            <Typography>
                                                                <strong>Activity:</strong>{" "}
                                                                {activity.name} ({activity.activity_id})
                                                            </Typography>
                                                        ) : null;
                                                    })()}

                                                    <Typography>
                                                        <strong>Plan Name:</strong>{" "}
                                                        {displayPlan?.plan_name || "-"}
                                                    </Typography>

                                                    <Typography>
                                                        <strong>Amount:</strong>{" "}
                                                        Rs. {
                                                            item?.booking_type === "booking"
                                                                ? getSingleAmount(item, "booking")
                                                                : getEnrollmentBookingAmount(booking)
                                                        }
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Grid>

                                    {/* Payment Info */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Payment Info</Typography>
                                        <Typography>
                                            <strong>Membership Amount:</strong> Rs. {getSingleAmount(item, "membership")}
                                        </Typography>
                                        <Typography>
                                            <strong>Enrollment Amount:</strong> Rs. {getItemEnrollmentAmount(item)}
                                        </Typography>
                                        {item?.difference_amount_paid ? (
                                            <Fragment>
                                                <Typography color={"error"}>
                                                    <strong>
                                                        Difference Amount Paid: Rs. {item?.difference_amount_paid || "-"}
                                                    </strong>
                                                </Typography>
                                                <Typography color={"error"}>
                                                    <strong>
                                                        Total Amount Paid: Rs. 
                                                        {parseInt(item?.amount_paid) +
                                                            parseInt(item?.difference_amount_paid) || "-"}
                                                    </strong>
                                                </Typography>
                                            </Fragment>
                                        ) : (
                                            <Typography color={"error"}>
                                                <strong>Total Amount Paid: Rs. {item?.amount_paid || "-"}</strong>
                                            </Typography>
                                        )}

                                        <Typography>
                                            <strong>Mode:</strong> {item.payment_mode}
                                        </Typography>
                                        <Typography>
                                            <strong>Order ID:</strong> {item?.order_id || "-"}
                                        </Typography>
                                        <Typography>
                                            <strong>Tracking ID:</strong> {paymentRes?.tracking_id || "-"}
                                        </Typography>
                                        <Typography>
                                            <strong>Date:</strong> {handleDateTimeDefault(item.createdAt)}
                                        </Typography>
                                        <Typography>
                                            <strong>Remarks:</strong>{" "}
                                            {/* {editingIndex === index ? (
                                                <Box>
                                                    <textarea
                                                        value={editedRemark}
                                                        onChange={(e) => setEditedRemark(e.target.value)}
                                                        rows={4}
                                                        style={{
                                                            width: "100%",
                                                            marginRight: "8px",
                                                            marginBottom: "8px",
                                                            padding: "8px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc",
                                                            fontFamily: "inherit",
                                                            fontSize: "14px",
                                                            resize: "vertical",
                                                        }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth={false}
                                                        onClick={async () => {
                                                            try {
                                                                await updateRemarkAPI(item._id, editedRemark); // replace with your actual API call
                                                                item.remarks = editedRemark; // optimistically update UI
                                                                setEditingIndex(null);
                                                            } catch (err) {
                                                                console.error("Failed to update remarks", err);
                                                            }
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        color="error"
                                                        fullWidth={false}
                                                        onClick={() => setEditingIndex(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            ) : ( */}
                                            {/* <Button
                                                        size="small"
                                                        fullWidth={false}
                                                        variant="contained"
                                                        onClick={() => {
                                                            setEditingIndex(index);
                                                            setEditedRemark(item?.remarks || "");
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>{" "} */}
                                            {item?.remarks || "-"}
                                        </Typography>

                                        {editingIndex === index && (
                                            <Grid container spacing={2} sx={{ mt: 2, mb: 2 }} alignItems="center">
                                                <Grid item xs={12} sm={12}>
                                                    <textarea
                                                        value={editedRemark || item?.remarks || ""}
                                                        onChange={(e) => setEditedRemark(e.target.value)}
                                                        rows={4}
                                                        style={{
                                                            width: "100%",
                                                            padding: "8px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc",
                                                            fontFamily: "inherit",
                                                            fontSize: "14px",
                                                            resize: "vertical",
                                                        }}
                                                        placeholder="Enter remarks"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <Input
                                                        id="difference_amount_paid"
                                                        name="difference_amount_paid"
                                                        label="Difference Amount Paid"
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value, 10);
                                                            setEditDifferenceAmount(
                                                                isNaN(value) || value < 0 ? null : value,
                                                            );
                                                        }}
                                                        value={differenceAmount || ""}
                                                        fullWidth
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        onClick={async () => {
                                                            try {
                                                                await updateRemarkAPI(item._id, {
                                                                    remarks: editedRemark,
                                                                    difference_amount_paid: differenceAmount,
                                                                }); // replace with your actual API call
                                                                item.remarks = editedRemark;
                                                                item.difference_amount_paid = differenceAmount;
                                                                setEditingIndex(null);
                                                            } catch (err) {
                                                                console.error(
                                                                    "Failed to update difference amount",
                                                                    err,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        color="error"
                                                        fullWidth
                                                        onClick={() => {
                                                            setEditingIndex(null);
                                                            setEditedRemark("");
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        )}

                                        <Button
                                            size="small"
                                            fullWidth={false}
                                            variant="contained"
                                            onClick={() => {
                                                setEditingIndex(index);
                                            }}
                                        >
                                            Add Difference Amount
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })
                )}
            </DialogContent>

            <EnrollActivityAddDrawer
                type="enrollment"
                show={showDrawer}
                close={() => [setShowDrawer(false), setBookingId(null), setBookingInitalValue(null), refetch()]}
                formType={isAuth().roles === "super" ? "Edit" : "View"}
                initialValues={bookingInitalValue}
                getActivityList={getActivityList}
                getMembersList={getMembersList}
            />
        </Dialog>
    );
};
